import React from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { EntityRelationMetadata } from "../EntityRelationsManager";
import { AddonParams } from "../../base/BaseAddon";
import {
  StepChangeEvent,
  StepType,
  ValidationError,
  ValidationErrorCode,
  ValidationResult,
  ValidationWarningCode
} from "../../../types/types";
import { TOASTER_TOAST_TIME } from "../../../../../../core/toaster/Toaster_model";
import toastService from "../../../../../../core/toaster/ToasterService";
import { observable } from "mobx";
import OrganisationsApi, {
  OrganisationsApi as IOrganisationsApi
} from "../../../../../../services/api/v2/organisations/Organisations.api";
import { StakeholderColDefFieldNamesEnum } from "../../../../../../core/grids/builder/enums/AgGridColDefFieldNameEnum";
import * as _ from "lodash";
import { BusinessAreaAddonView } from "./BusinessAreaAddon_view";
import { EntityRelationAddon } from "../EntityRelationAddon";
import { NoDataFound } from "../../../components/NotFound/NoDataFound";

export interface UploaderBusinessAreaEntity extends FP.Entities.IBusinessArea {
  isNew: boolean;
  isMatched: boolean;
  matchedId: number;
}

export const BUSINESS_AREA_ENTITY_TYPE = "business_area";

export const businessAreaMetadata: EntityRelationMetadata = {
  entityType: BUSINESS_AREA_ENTITY_TYPE,
  displayName: "Business Areas",
  dependencies: [],
  requiredForEntities: ["role", "stakeholder"]
};

export interface BusinessArea {
  id?: string | number;
  name: string;
  slug?: string;
  description?: string;
  isNew?: boolean;
  isMatched?: boolean;
  matchedId?: string | number;
}

export class BusinessAreaAddon extends EntityRelationAddon {
  @observable data: FP.Entities.IBusinessArea[];
  organisationProvider: IOrganisationsApi;
  organisationId: number;
  onBack: any;

  constructor(onNext: (event: StepChangeEvent) => void, onBack: () => void, params: AddonParams) {
    super("BusinessAreaAddon", BUSINESS_AREA_ENTITY_TYPE, businessAreaMetadata, onNext, onBack, params);
    this.organisationProvider = OrganisationsApi;
    this.organisationId = params.organisationId;
    this.onBack = onBack;
  }
  async getExistingData(): Promise<FP.Entities.IBusinessArea[]> {
    try {
      const res = await this.organisationProvider.getBusinessAreas(this.organisationId);
      return res.payload || [];
    } catch (error) {
      console.error("Error fetching existing business areas:", error);
      return [];
    }
  }

  // temp
  async getExistingEntities(): Promise<FP.Entities.IBusinessArea[]> {
    return this.getExistingData();
  }

  async mapEntities(
    uploadedData: any[],
    existingEntities: Partial<UploaderBusinessAreaEntity>[]
  ): Promise<Partial<UploaderBusinessAreaEntity>[]> {
    const extractedAreas = this.extractBusinessAreasFromData(uploadedData);
    return extractedAreas.map(area => {
      const existingArea = existingEntities.find(
        existing => existing.name.toLowerCase() === area.name.toLowerCase() || existing.slug === area.slug
      );

      if (existingArea) {
        return {
          ...existingArea,
          isNew: false,
          isMatched: true,
          matchedId: existingArea.id
        };
      }

      return {
        ...area,
        isNew: true,
        isMatched: false
      };
    });
  }

  extractBusinessAreasFromData(data: any[]): Partial<UploaderBusinessAreaEntity>[] {
    const uniqueAreas = new Set<string>();

    data.forEach(row => {
      const businessAreaValue =
        row[StakeholderColDefFieldNamesEnum.BusinessArea] || row.business_area || row.businessArea || row.BusinessArea;

      if (businessAreaValue && typeof businessAreaValue === "string" && businessAreaValue.trim()) {
        uniqueAreas.add(businessAreaValue.trim());
      }
    });

    return Array.from(uniqueAreas).map(name => ({
      name,
      slug: _.camelCase(name),
      description: ""
    }));
  }

  validateEntities(mappedEntities: UploaderBusinessAreaEntity[]): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];

    mappedEntities.forEach(entity => {
      if (!entity.name || entity.name.trim() === "") {
        errors.push({
          message: `Business area name is required`,
          code: ValidationErrorCode.MISSING_NAME,
          entity
        });
      }

      if (entity.isNew) {
        warnings.push({
          message: `New business area will be created: ${entity.name}`,
          code: ValidationWarningCode.NEW_ENTITY,
          entity
        });
      }
    });

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  validateMappingResult(result: UploaderBusinessAreaEntity[]): ValidationResult {
    return this.validateEntities(result);
  }

  protected getJSXElement(step: StepType, state: any): React.ReactElement {
    if (!state.data) {
      return <NoDataFound onBack={this.onBack}></NoDataFound>;
    }

    return (
      <BusinessAreaAddonView
        uploadedData={state.uploadedData || state.data || []}
        processedEntities={state.processedEntities || {}}
        onComplete={this.handleComplete}
      />
    );
  }

  private handleComplete = (businessAreas: UploaderBusinessAreaEntity[]) => {
    const validationResult = this.validateEntities(businessAreas);

    if (!validationResult.valid) {
      toastService
        .showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right")
        .setContent(<span>{validationResult.errors[0].message}</span>);
      return;
    }

    if (validationResult.warnings.length > 0) {
    }

    if (this.onNext) {
      this.onNext({
        type: StepType.entityRelationValidation,
        data: businessAreas
      });
    }
  };
}
